import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Formik, Field } from 'formik';
import moment from 'moment-timezone';
import _get from 'lodash/get';
import toSnakeCase from 'lodash/snakeCase';
import _capitalize from 'lodash/capitalize';

import { history } from '../../../helpers/history';

import OutlinedTextField from '../../shared/Fields/OutlinedTextField';
import StepperButtons from '../../shared/StepperButtons';
import SimpleSelect from '../../shared/Selects/SimpleSelect';
import mapListToOptions from '../../../helpers/mapListToOptions';
import { doctypes } from '../../../constants/enums';
import OurDatePicker from '../../shared/CustomDatePicker';
import SegmentHeader from '../../shared/SegmentHeader';
import { validationSchema } from './PortalCosignerDataValidation';
import ActivityIndicator from '../../shared/ActivityIndicator';
import PrimaryButton from '../../shared/Buttons/PrimaryButton';
import CustomDialog from '../../shared/Modals/CustomDialog';
import formatMoney from '../../../helpers/formatMoney';
import QuotaNoHolder from '../QueryQuotaNoHolder/QuotaNoHolder';

class PortalCosignerDataPage extends PureComponent {
  state = {
    isGnpSale: false,
    isGeneralWarrantySale: false,
    validateGeneralWarrantySaleConfirmationUse: false,
    availableQuota: -1,
    assignedQuota: -1,
    usedQuota: -1,
    assignedQuotaForEspecialArticles: -1,
    availableLowCategoryQuota: -1,
    megacupoResponseId: null,
  };
  currentStep = this.props.routes.indexOf(this.props.location.pathname);
  nextStep = this.currentStep + 1;

  componentDidMount() {
    const { specialSale } = this.props;
    this.props.setCurrentStep(this.currentStep);
    if (
      (this.props.promissory &&
        !this.props.promissory.cosigner &&
        !this.props.transfer.requestMade) || (!this.props.requiresCosigner && this._isRemoteSale()) || this.props.isCommercialSale
    ) {
      this.props.autoSkipStep(this.props.location.pathname);
      this.skipCosigner();
    }
    if (specialSale && (specialSale.type === 'GNP')) {
      this.setState({ isGnpSale: true });
    }
  }

  _isRemoteSale = () => {
    return (
      this.props.process === 'remoteDigitalSaleWithUUP' ||
      this.props.process === 'remoteDigitalSaleWithoutUUP'
    );
  };

  skipCosigner = () => {
    const { debtor } = this.props;

    this.props.resetCosignerData({
      debtorContractNumber: debtor.debtor_contract_number,
      debtorIdentification: debtor.debtor_identification,
      debtorIdentificationType: debtor.debtor_identification_type,
      debtorBirthdate: debtor.debtor_birthdate,
      isGeneralWarrantySale: this.state.isGeneralWarrantySale
    }, this.state.isGeneralWarrantySale);
    this.props.setNextStep(this.props.process);
    history.push(this.props.routes[this.nextStep]);
  };

  isGeneralWarrantySaleActive = () => {
    const saleType = toSnakeCase(window.location.pathname.split('/')[1]);
    return this.props.generalWarrantySaleActive[saleType];
  };

  closeNoHolderModal = () => {
    this.setState({ validateGeneralWarrantySaleConfirmationUse: false });
  }

  onSubmit = values => {
    const {
      contract,
      cosignerDoctype,
      cosignerDocid,
      birthday,
      solidaryDebtorCausal
    } = values;

    const {
      debtor_contract_number,
      debtor_identification_type,
      debtor_identification,
      debtor_birthdate
    } = this.props.debtor;

    this.props.checkThirdStep(
      {
        debtor_contract_number,
        debtor_identification_type,
        debtor_identification,
        debtor_birthdate,
        cosigner_contract_number: contract ? Number(contract) : null,
        cosigner_identification_type: cosignerDoctype,
        cosigner_identification: cosignerDocid.toString(),
        cosigner_birthdate: birthday
          ? moment(birthday, 'YYYY/MM/DD').format('YYYY-MM-DD')
          : null,
        reason_solidary_debtor: solidaryDebtorCausal.toString()
      },
      this.props.routes[this.nextStep],
      this.props.process,
      this.state.isGeneralWarrantySale
    );
  };

  docTypesWithoutIdentificationCard = () => {
    return doctypes.filter(docType => docType.name !== 'Tarjeta de Identidad');
  };

  requestGeneralWarrantySaleToScore = () => {
    const {
      debtor_identification_type,
      debtor_identification,
      debtor_contract_number,
      debtor_expedition_date,
      debtor_birthdate
    } = this.props.debtor;

    const isAgeValid = Math.floor(moment().diff(debtor_birthdate, 'years', true)) <= this.props.debtorMaxAge;

    if (!isAgeValid) {
      this.props.newAlert(
        'error',
        'ERROR:',
        'El cliente supera la edad máxima permitida para venta a no títular sin codeudor.'
      );
      return;
    }

    const canSellWithGeneralWarranty = this.props.pointOfSales.some(p => p.canSellWithGeneralWarranty);

    if (!canSellWithGeneralWarranty) {
      this.props.newAlert(
        'error',
        'ERROR:',
        'El punto de venta no está autorizado para realizar ventas a no títular sin codeudor.'
      );
      return;
    }

    this.props.checkGeneralWarrantySaleScore({
      identification: debtor_identification,
      typeOfIdentification: debtor_identification_type,
      contractId: debtor_contract_number
    }).then(response => {
      if (response.valid) {
        this.props.getAvailableQuotaNoHolder({
          contract: debtor_contract_number,
          identification: debtor_identification,
          identificationType: debtor_identification_type,
          debtorBirthday: debtor_birthdate,
          debtorDocumentExpeditionDate: debtor_expedition_date
        }).then(data => {
          if (data) {
            const [lowCategoryConfig, highCategoryConfig] = this.props.generalWarrantyCategoriesConfig;

            const assignedQuota = Math.min(highCategoryConfig.max_value, data.megaQuotas[0].quota);
            const usedQuota = data.usedQuota + data.usedQuotaByClient;

            const availableQuota = Math.max(Math.min(highCategoryConfig.max_value, assignedQuota) - usedQuota, 0);
            const availableLowCategoryQuota = Math.max(Math.min(lowCategoryConfig.max_value, assignedQuota) - usedQuota, 0);

            this.setState({
              availableLowCategoryQuota,
              availableQuota,
              assignedQuota,
              usedQuota,
              megacupoResponseId: data.megaQuotas[0].id,
              validateGeneralWarrantySaleConfirmationUse: true
            });
          }
        });
      }
    });
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      classes,
      serverErrors = {},
      requiresCosigner,
      holder,
      cosigner,
      promissory,
      transfer,
      solidaryDebtorCauses = [],
      loading,
      canSellSecureSaleByZone,
      canCheckGeneralWarrantyScore,
      isGeneralWarrantyWithScoreActive,
      generalWarrantyCategoriesConfig,
      categories = []
    } = this.props;

    const canSellGeneralWarranty = !holder && this.isGeneralWarrantySaleActive() && canSellSecureSaleByZone && isGeneralWarrantyWithScoreActive && canCheckGeneralWarrantyScore ;

    const {
      isGnpSale,
      isGeneralWarrantySale,
      assignedQuota,
      usedQuota,
      availableQuota,
      megacupoResponseId,
      availableLowCategoryQuota
    } = this.state;

    const promissoryCosigner = _get(promissory, 'cosigner', false);

    if (promissory && !promissoryCosigner && !transfer.requestMade || categories.length === 0) {
      return <ActivityIndicator />;
    }

    const [lowCategoryConfig, highCategoryConfig] = generalWarrantyCategoriesConfig;
    const hasNoAvailableQuota = availableQuota <= 0 && availableLowCategoryQuota <= 0;

    return (
      <div className={classes.container}>
        <Paper className={classes.paper}>
          <Formik
            initialValues={{
              contract:
                transfer.contract ||
                _get(promissory, 'cosigner_contract') ||
                (requiresCosigner ? cosigner.contract || '' : ''),
              cosignerDoctype:
                transfer.identificationType ||
                _get(promissoryCosigner, 'type_of_identification') ||
                cosigner.identificationType ||
                'Cédula',
              cosignerDocid:
                transfer.identification ||
                _get(promissoryCosigner, 'identification') ||
                cosigner.identification ||
                '',
              birthday:
                transfer.birthdate ||
                (cosigner.birthdate
                  ? moment(cosigner.birthdate, 'YYYY-MM-DD').format(
                    'YYYY/MM/DD '
                  )
                  : _get(promissoryCosigner, 'identification')
                    ? moment(
                      promissoryCosigner.date_of_birth,
                      'YYYY-MM-DD'
                    ).format('YYYY/MM/DD ')
                    : null),
              solidaryDebtorCausal: requiresCosigner || promissoryCosigner ? 'REQUIRES COSIGNER' : cosigner.causal || '',
              requireContract: requiresCosigner && !promissoryCosigner
            }}
            validationSchema={validationSchema}
            onSubmit={this.onSubmit}
            onReset={(_, actions) => {
              actions.resetForm();
              this.setState({ isGeneralWarrantySale: !isGeneralWarrantySale });
            }}
            render={({
              values,
              touched,
              errors,
              handleSubmit,
              handleReset,
              handleBlur,
              setFieldValue
            }) => (
              <form className={classes.form}>
                <SegmentHeader className={classes.segmentHeader}>
                  {(transfer.contract && 'INFORMACIÓN CODEUDOR (TRASLADO)') ||
                    (requiresCosigner
                      ? 'INFORMACIÓN CODEUDOR'
                      : promissoryCosigner
                        ? 'INFORMACIÓN CODEUDOR (PAGARÉ ÚNICO)'
                        : 'CODEUDOR SOLIDARIO (OPCIONAL)')}
                </SegmentHeader>
                <Grid container spacing={24} alignItems="flex-start">
                  {!requiresCosigner &&
                    !promissoryCosigner &&
                    !transfer.contract && (
                      <Grid item sm={6} xs={12}>
                        <Field
                          name="solidaryDebtorCausal"
                          render={({ field }) => {
                            return (
                              <SimpleSelect
                                label="Causal:"
                                options={[
                                  {
                                    id: -1,
                                    key: -1,
                                    value: '',
                                    label: '--'
                                  },
                                  ...mapListToOptions(solidaryDebtorCauses)
                                ]}
                                onChange={e => {
                                  field.onChange(e);
                                  values.cosignerDocid = '';
                                  values.birthday = '';
                                }}
                                value={field.value}
                                name={field.name}
                                disabled={isGnpSale}
                              />
                            );
                          }}
                        />
                      </Grid>
                    )}
                  {(requiresCosigner ||
                    promissoryCosigner ||
                    transfer.contract) && (
                      <Grid item sm={6} xs={12}>
                        <Field
                          name="contract"
                          render={({ field }) => {
                            return (
                              <OutlinedTextField
                                disabled={
                                  !!promissoryCosigner || !!transfer.contract || !!isGeneralWarrantySale
                                }
                                label="Numero de contrato:"
                                type={'number'}
                                {...field}
                                error={
                                  (touched.contract && !!errors.contract) ||
                                  serverErrors.contract
                                }
                                helperText={
                                  (touched.contract && errors.contract) ||
                                  serverErrors.contract
                                }
                              />
                            );
                          }}
                        />
                      </Grid>
                    )}
                  <Grid item sm={6} xs={12}>
                    <Field
                      name="cosignerDoctype"
                      render={({ field }) => {
                        return (
                          <SimpleSelect
                            disabled={
                              !values.solidaryDebtorCausal ||
                              !!promissoryCosigner ||
                              !!transfer.contract ||
                              !!isGeneralWarrantySale
                            }
                            label="Tipo de identificacion del codeudor:"
                            options={mapListToOptions(
                              this.docTypesWithoutIdentificationCard(),
                              {},
                              true
                            )}
                            onChange={field.onChange}
                            value={field.value}
                            name={field.name}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Field
                      name="cosignerDocid"
                      render={({ field }) => {
                        return (
                          <OutlinedTextField
                            disabled={
                              !values.solidaryDebtorCausal ||
                              !!promissoryCosigner ||
                              !!transfer.contract ||
                              !!isGeneralWarrantySale
                            }
                            label="Identificacion del codeudor:"
                            error={
                              !!values.solidaryDebtorCausal &&
                              ((touched.cosignerDocid &&
                                !!errors.cosignerDocid) ||
                                (serverErrors &&
                                  !!serverErrors.cosigner_identification))
                            }
                            helperText={
                              !!values.solidaryDebtorCausal &&
                              ((touched.cosignerDocid &&
                                errors.cosignerDocid) ||
                                (serverErrors &&
                                  serverErrors.cosigner_identification))
                            }
                            {...field}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <OurDatePicker
                      disabled={
                        !values.solidaryDebtorCausal ||
                        !!promissoryCosigner ||
                        !!transfer.contract ||
                        !!isGeneralWarrantySale
                      }
                      name={'birthday'}
                      invalidLabel="Fecha inválida"
                      maxDateMessage="Fecha inválida"
                      minDateMessage="Fecha inválida"
                      invalidDateMessage={'Fecha inválida'}
                      label="Fecha nacimiento codeudor:"
                      value={values.birthday}
                      onChange={e => setFieldValue('birthday', e)}
                      onBlur={handleBlur}
                      onError={() => setFieldValue('birthday', null)}
                      error={
                        !!values.solidaryDebtorCausal &&
                        (!!serverErrors.cosigner_birthdate ||
                          (touched.birthday && !!errors.birthday))
                      }
                      helperText={
                        !!values.solidaryDebtorCausal &&
                        (serverErrors.cosigner_birthdate ||
                          (touched.birthday && errors.birthday))
                      }
                      fullWidth
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <div className={!canSellGeneralWarranty ? classes.bottom : classes.bottomWithGeneralWarranty}>
                  {canSellGeneralWarranty && (
                    <Grid item xs={12} md={6}>
                      <PrimaryButton
                        variant="contained"
                        className={classes.button}
                        disabled={false}
                        onClick={this.requestGeneralWarrantySaleToScore}
                        loading={loading}
                      >
                        Venta sin codeudor
                      </PrimaryButton>
                    </Grid>
                  )}
                  <StepperButtons
                    onSubmit={handleSubmit}
                    goBack={this.props.goBack}
                    skip={() => this.skipCosigner()}
                    allowOptional={
                      !transfer.contract &&
                      !requiresCosigner &&
                      !values.solidaryDebtorCausal ||
                      !!isGeneralWarrantySale
                    }
                    allowNext={(!!values.solidaryDebtorCausal || transfer.contract) && !isGeneralWarrantySale}
                  />
                </div>
              </form>
            )}
          />
          <CustomDialog
            open={
              this.props.isGeneralWarrantyWithScoreActive &&
              this.state.validateGeneralWarrantySaleConfirmationUse
            }
            onClose={this.closeNoHolderModal}
            onConfirm={() => {
              if (hasNoAvailableQuota) {
                this.closeNoHolderModal();
                return;
              }

              this.setState({ validateGeneralWarrantySaleConfirmationUse: false, isGeneralWarrantySale: true }, () => {
                this.props.changeQuotas({ assignedQuota, usedQuota, availableQuota, megacupoResponseId });
                this.skipCosigner();
              });
            }}
            onConfirmSecond={this.closeNoHolderModal}
            title="Notificación - Venta a no títular sin codeudor"
            secondButtonText={hasNoAvailableQuota ? "" : "VOLVER"}
            buttonText={hasNoAvailableQuota ? "VOLVER" : "ACEPTAR"}
            disableBackdropClick={true}
          >
            {hasNoAvailableQuota ? (
              <p>Cliente no aplica para venta a no títular sin codeudor</p>
            ) : (
              <QuotaNoHolder
                serverErrors={serverErrors}
                availableQuota={availableQuota}
                availableLowCategoryQuota={availableLowCategoryQuota}
                caseThreeConfig={generalWarrantyCategoriesConfig}
                categories={categories}
              />
            )}
          </CustomDialog>
        </Paper>
      </div>
    );
  }
}

const styles = theme => ({
  container: {
    display: 'flex'
  },
  paper: {
    flexGrow: 1
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-around'
  },
  bottom: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end'
  },
  bottomWithGeneralWarranty: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  checkbox: {
    marginTop: 3 * theme.spacing.unit,
    marginLeft: theme.spacing.unit / 2,
  },
  button: {
    alignSelf: 'flex-end',
    marginTop: 2 * theme.spacing.unit,
    paddingLeft: 8 * theme.spacing.unit,
    paddingRight: 8 * theme.spacing.unit
  },
  segmentHeader: {
    width: '100%',
    marginTop: 3 * theme.spacing.unit,
    marginBottom: 2 * theme.spacing.unit,
    '&:first-of-type': {
      marginTop: 0
    }
  }
});

PortalCosignerDataPage.propTypes = {
  classes: PropTypes.object.isRequired,
  newAlert: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired
};

export default withStyles(styles)(PortalCosignerDataPage);
